/**
 * Helpers UI dùng chung cho các view Sales Control. Format date/number theo Access
 * (`DD-MMM-YYYY`, `Standard`, `Percent`) — bám `form_frmCtrlSalesQry*.txt`.
 */
import { titleCaseName } from '../../utils/text'

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function formatAccessDate(value: unknown): string {
  if (value == null || value === '') return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

export function formatAccessDateTime(value: unknown): string {
  if (value == null || value === '') return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

export function formatAccessNumber(value: number): string {
  if (!Number.isFinite(value)) return ''
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function formatAccessPercent(value: number): string {
  if (!Number.isFinite(value)) return ''
  return `${(value * 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
}

export type SaleControlColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'percent'

/** Override auto-detect per-column filter kind (template dataList). */
export type SaleControlColumnFilterKind = 'text' | 'checkboxes' | 'date' | 'number' | 'none'

export interface SaleControlColumnDef {
  key: string
  label: string
  kind?: SaleControlColumnKind
  align?: 'left' | 'right' | 'center'
  width?: number
  /** Long text → wrap + row auto-grows (gold list `/sales/contract/list`). */
  wrap?: boolean
  /** Proper name lưu HOA trong DB (cp_vn) → render Title Case (display-only). */
  proper?: boolean
  /** Override auto-detect per-column filter kind (▽ header). */
  filter?: SaleControlColumnFilterKind
}

export function formatCell(row: Record<string, unknown>, col: SaleControlColumnDef): string {
  const v = row[col.key]
  if (v == null) return ''
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'datetime') return formatAccessDateTime(v)
  if (col.kind === 'percent' && typeof v === 'number') return formatAccessPercent(v)
  if (col.kind === 'number' && typeof v === 'number') return formatAccessNumber(v)
  if (typeof v === 'number') return formatAccessNumber(v)
  if (col.proper) return titleCaseName(v)
  return String(v)
}

/** Y/N text mapping (`finalized_text`, `ctrl_filldoc_txt`, …). */
export function isYesText(value: unknown): boolean {
  if (value == null) return false
  return String(value).trim().toUpperCase() === 'Y'
}
